import { useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Phone,
  Upload,
  X,
  CheckCircle2,
  ShieldCheck,
  Lock,
  Home as HomeIcon,
  MessageSquare,
} from 'lucide-react';
import LandingNav, { PHONE_DISPLAY, PHONE_TEL } from '@/components/LandingNav';
import LandingFooter from '@/components/LandingFooter';
import QuoteVerifyForm from '@/components/QuoteVerifyForm';
import SchemaMarkup from '@/components/SchemaMarkup';
import { buildWebPageSchema } from '@/lib/schema';
import HowItWorks5 from '@/components/HowItWorks5';
import SEO from '@/components/SEO';

const trackPixel = (event, data = {}) => {
  if (window.fbq) {
    if (['Lead', 'InitiateCheckout', 'CompleteRegistration'].includes(event)) {
      window.fbq('track', event, data);
    } else {
      window.fbq('trackCustom', event, data);
    }
  }
};
const trackGA = (event, data = {}) => {
  if (window.gtag) window.gtag('event', event, data);
};

export default function LandingNoSales() {
  useEffect(() => {
    trackPixel('ViewContent', { content_name: 'Landing No Sales' });
    trackGA('view_landing_no_sales', {
      event_category: 'paid_traffic',
      event_label: 'no_sales',
    });
  }, []);

  const scrollToForm = (location) => {
    trackGA('cta_click_no_sales', {
      event_category: 'engagement',
      event_label: location,
    });
    document.querySelector('[data-testid="verify-section"]')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      <SEO
        title="Impact Windows in South Florida — No In-Home Sales Appointment Required"
        description="Get an impact window quote without sitting through a 2–3 hour in-home sales presentation. Upload photos, verify by SMS, and receive Good/Better/Premium pricing."
        path="/no-sales-impact-windows"
      />
      <SchemaMarkup
        schemas={buildWebPageSchema({
          url: '/no-sales-impact-windows',
          name: 'Impact Windows in South Florida — No In-Home Sales Appointment Required',
          description:
            'Get an impact window quote without sitting through a 2–3 hour in-home sales presentation. Upload photos, verify by SMS, and receive Good/Better/Premium pricing.',
        })}
        instanceId="lp-no-sales"
      />
      <LandingNav logoTo="/no-sales-impact-windows" />

      {/* Hero — no-appointment focused */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[var(--brand-ocean-100)]/40 via-white to-white pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 pt-12 pb-10 md:pt-16 md:pb-14 text-center">
          <Badge
            variant="outline"
            className="rounded-full px-3 py-1 text-[11px] uppercase tracking-wider border-[var(--brand-ocean-600)] text-[var(--brand-ocean-700)] bg-white"
          >
            South Florida • No Sales Visit Required
          </Badge>
          <h1
            className="mt-4 font-heading text-4xl sm:text-5xl md:text-6xl font-semibold leading-[1.05] tracking-tight text-[var(--brand-slate-900)]"
            data-testid="hero-headline"
          >
            Get Installed Impact Window Pricing —{' '}
            <span className="text-[var(--brand-ocean-700)]">Without a Sales Visit</span>
          </h1>
          <p
            className="mt-5 text-lg sm:text-xl text-[var(--brand-slate-600)] max-w-2xl mx-auto"
            data-testid="hero-subheadline"
          >
            Upload photos of your openings and get Good / Better / Premium installed pricing — no in-home sales appointment.
          </p>

          <ul className="mt-7 inline-flex flex-col sm:flex-row gap-3 sm:gap-6 text-left text-[var(--brand-slate-800)]" data-testid="hero-bullets">
            {['No in-home appointment', 'Final measurements confirmed before ordering', 'Permits & install included'].map((b) => (
              <li key={b} className="flex items-start gap-2">
                <span className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-[var(--brand-ocean-100)] text-[var(--brand-ocean-700)]">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                </span>
                <span className="text-sm sm:text-[15px]">{b}</span>
              </li>
            ))}
          </ul>

          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              size="lg"
              onClick={() => scrollToForm('hero')}
              data-testid="hero-cta-upload-photos"
              className="h-14 px-7 rounded-xl text-base font-semibold bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] shadow-md hover:shadow-lg"
            >
              <Upload className="mr-2 h-5 w-5" />
              Get My Price
            </Button>
            <a
              href={PHONE_TEL}
              className="inline-flex items-center justify-center h-14 px-6 rounded-xl text-base font-semibold border-2 border-[hsl(var(--border))] text-[var(--brand-slate-800)] hover:bg-[hsl(var(--secondary))]"
              onClick={() => trackGA('phone_call_click', { event_category: 'engagement', event_label: 'hero_call_no_sales' })}
              data-testid="hero-call-cta"
            >
              <Phone className="mr-2 h-5 w-5" />
              Or call {PHONE_DISPLAY}
            </a>
          </div>
        </div>
      </section>

      {/* Comparison: Traditional vs Snap */}
      <section className="py-14 sm:py-20" data-testid="comparison-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-wider text-[var(--brand-ocean-700)]">
              Why we built this
            </p>
            <h2 className="mt-2 font-heading text-3xl sm:text-4xl font-semibold text-[var(--brand-slate-900)]">
              The traditional process is broken.
            </h2>
            <p className="mt-3 text-[var(--brand-slate-600)]">
              You shouldn’t have to give up an evening — or sit through a sales pitch — just to find out what windows cost.
            </p>
          </div>

          <div className="mt-8 sm:mt-10 grid md:grid-cols-2 gap-5">
            <Card className="rounded-2xl border-2 border-[hsl(var(--border))] bg-white" data-testid="comparison-traditional">
              <CardContent className="p-6 sm:p-7">
                <p className="text-xs font-semibold uppercase tracking-wider text-[var(--brand-slate-500)]">
                  Traditional companies
                </p>
                <h3 className="mt-1 font-heading text-xl font-semibold text-[var(--brand-slate-900)]">
                  60–90 minutes in your living room
                </h3>
                <ul className="mt-5 space-y-3">
                  {[
                    'Mandatory in-home appointment before any pricing',
                    'High-pressure “sign tonight” discounts',
                    'One brand pushed regardless of fit',
                    'Pricing held hostage until the end of the pitch',
                    'Days of follow-up calls if you don’t buy',
                  ].map((t) => (
                    <li key={t} className="flex items-start gap-3 text-sm text-[var(--brand-slate-700)]">
                      <span className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-red-50 text-red-600 shrink-0">
                        <X className="h-3.5 w-3.5" />
                      </span>
                      {t}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card className="rounded-2xl border-2 border-[var(--brand-ocean-600)] bg-[var(--brand-ocean-100)]/15 relative overflow-hidden" data-testid="comparison-snap">
              <div className="absolute -top-px right-6">
                <Badge className="rounded-b-md rounded-t-none bg-[var(--brand-ocean-700)] text-white border-0 px-3 py-1 text-[11px]">
                  Snap Windows
                </Badge>
              </div>
              <CardContent className="p-6 sm:p-7">
                <p className="text-xs font-semibold uppercase tracking-wider text-[var(--brand-ocean-700)]">
                  The Snap way
                </p>
                <h3 className="mt-1 font-heading text-xl font-semibold text-[var(--brand-slate-900)]">
                  Photos in. Pricing out. ~24 hours.
                </h3>
                <ul className="mt-5 space-y-3">
                  {[
                    'No in-home visit required to get pricing',
                    'Good / Better / Premium options side-by-side',
                    'Multiple manufacturers — we recommend the right fit',
                    'Email-first follow-up, only call if you ask',
                    'Phone-verified leads keep your info private',
                  ].map((t) => (
                    <li key={t} className="flex items-start gap-3 text-sm text-[var(--brand-slate-800)]">
                      <span className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-[var(--brand-ocean-700)] text-white shrink-0">
                        <CheckCircle2 className="h-3.5 w-3.5" />
                      </span>
                      {t}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="border-y border-[hsl(var(--border))] bg-[hsl(var(--secondary))]/40" data-testid="trust-strip">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-5 sm:py-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-6">
            {[
              { icon: ShieldCheck, text: 'Licensed & insured South Florida contractor' },
              { icon: Lock, text: 'Your phone is verified, never sold' },
              { icon: MessageSquare, text: 'Email or text — only call if you ask' },
            ].map((item) => (
              <div key={item.text} className="flex items-center gap-2.5 text-sm sm:text-[15px] text-[var(--brand-slate-700)] justify-start sm:justify-center">
                <item.icon className="h-4 w-4 text-[var(--brand-ocean-700)] shrink-0" />
                <span className="font-medium">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Form */}
      <QuoteVerifyForm sourceLabel="no_sales" />

      {/* Real proof */}
      <section className="py-14 sm:py-20" data-testid="proof-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <p className="text-xs font-semibold uppercase tracking-wider text-[var(--brand-ocean-700)]">Real proof</p>
          <h2 className="mt-2 font-heading text-3xl sm:text-4xl font-semibold text-[var(--brand-slate-900)]">
            Real Projects, Real Pricing
          </h2>
          <p className="mt-3 text-[var(--brand-slate-600)]">
            Both of these homeowners got pricing without a single in-home appointment.
          </p>
          <div className="mt-7 sm:mt-9 divide-y divide-[hsl(var(--border))]">
            {[
              { id: 'kendall', title: 'Kendall Full Home', stat: '~$28,400', desc: 'Complete impact window and door replacement using ES Windows' },
              { id: 'north-miami', title: 'North Miami Condo', stat: '~$25,000', desc: 'HOA-compliant installation with coastal-grade materials (Mr. Glass)' },
            ].map((cs) => (
              <div key={cs.id} className="py-5 sm:py-6 first:pt-0 last:pb-0" data-testid={`proof-item-${cs.id}`}>
                <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                  <h3 className="font-heading text-lg sm:text-xl font-semibold text-[var(--brand-slate-900)]">{cs.title}</h3>
                  <span className="font-heading text-lg sm:text-xl font-semibold text-[var(--brand-ocean-700)] whitespace-nowrap">— {cs.stat}</span>
                </div>
                <p className="mt-1.5 text-sm sm:text-[15px] text-[var(--brand-slate-600)] leading-relaxed">{cs.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works (refresh, compact) */}
      <HowItWorks5 src="no_sales_how_it_works" variant="compact" />

      {/* Final CTA */}
      <section className="py-14 sm:py-20 bg-[hsl(var(--secondary))]/40" data-testid="final-cta-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="rounded-3xl border-2 border-[var(--brand-ocean-600)] bg-white p-8 sm:p-12 text-center shadow-sm">
            <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-[var(--brand-ocean-100)] mb-5">
              <HomeIcon className="h-7 w-7 text-[var(--brand-ocean-700)]" />
            </div>
            <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-[var(--brand-slate-900)]">
              No appointment. No pressure. Just pricing.
            </h2>
            <p className="mt-3 text-base sm:text-lg text-[var(--brand-slate-700)] max-w-xl mx-auto leading-relaxed">
              Upload a few photos and we’ll send your Good, Better, and Premium options — usually within 24 hours.
            </p>
            <div className="mt-8">
              <Button
                size="lg"
                onClick={() => scrollToForm('final_cta')}
                data-testid="final-cta-button"
                className="h-14 px-8 rounded-xl text-base font-semibold bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] shadow-md"
              >
                <Upload className="mr-2 h-5 w-5" />
                Get My Price
              </Button>
            </div>
            <div className="mt-5 flex flex-wrap justify-center gap-x-6 gap-y-2 text-xs sm:text-sm text-[var(--brand-slate-600)]">
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-[var(--brand-ocean-700)]" />No appointment</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-[var(--brand-ocean-700)]" />No pressure</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-[var(--brand-ocean-700)]" />Quote in ~24 hours</span>
            </div>
          </div>
        </div>
      </section>

      <LandingFooter />
    </div>
  );
}
